import { z } from 'zod';

import type { IPlayerProxy } from '@src/controllers/PlayerWrapper';
import PlayerError, { AdError } from '@src/errors/PlayerError';
import { getIsBrowser } from '@src/utils/device';

import { Theme } from './theme';

const isBrowser = getIsBrowser();

const VideoIdSchema = z.string();

const VideoObjSchema = z.object({
  url: z.string(),
  img: z.optional(z.string()),
  title: z.string(),
  description: z.optional(z.string()),
});

export const SourceOptionSchema = z.object({
  videoId: z.optional(VideoIdSchema),
  videoID: z.optional(VideoIdSchema), // still support legacy videoID, but cannot be used together with videoId
  video: z.optional(VideoObjSchema),
});

const VideoJsEventCallbackSchema = z
  .function()
  .args(isBrowser ? z.instanceof(CustomEvent) : z.any());

const VideoJsOnInitCallbackSchema = z
  .function()
  .args(z.optional(isBrowser ? z.instanceof(Proxy) : z.any()));

const VideoJsOnErrorCallbackSchema = z
  .function()
  .args(
    z.optional(
      z.union([
        isBrowser ? z.instanceof(Error) : z.any(),
        isBrowser ? z.instanceof(PlayerError) : z.any(),
        z.null(),
      ])
    ),
    z.optional(isBrowser ? z.instanceof(CustomEvent).nullish() : z.any())
  );

const VideoJsOnAdErrorCallbackSchema = z
  .function()
  .args(z.optional(isBrowser ? z.instanceof(AdError) : z.any()));

export const OnEventCallbackSchema = z
  .function()
  .args(z.string(), z.string(), z.string());

export const OtherOptionsSchema = z.object({
  mediaServerURL: z.optional(z.string()),
  onInit: z.optional(VideoJsOnInitCallbackSchema), // TODO check callback
  onError: z.optional(VideoJsOnErrorCallbackSchema), // TODO check callback
  onAdError: z.optional(VideoJsOnAdErrorCallbackSchema), // TODO check callback
  onPlaying: z.optional(VideoJsEventCallbackSchema),
  onPlay: z.optional(VideoJsEventCallbackSchema),
  onLoadStart: z.optional(VideoJsEventCallbackSchema),
  onCanPlay: z.optional(VideoJsEventCallbackSchema),
  onPause: z.optional(VideoJsEventCallbackSchema),
  onEnded: z.optional(VideoJsEventCallbackSchema),
  onVolumeChange: z.optional(VideoJsEventCallbackSchema),
  onPlayerResize: z.optional(VideoJsEventCallbackSchema),
  onResize: z.optional(VideoJsEventCallbackSchema),
  onFullscreenChange: z.optional(VideoJsEventCallbackSchema),
  onEvent: z.optional(OnEventCallbackSchema),
  trackingHandlers: z.optional(z.record(VideoJsEventCallbackSchema)),
  trackingScheme: z.optional(z.enum(['v3', 'v3_ad'])),
  defaultTrackingCategory: z.optional(z.string()),
  eventTypesToTrack: z.optional(z.array(z.string())),
  aspectRatio: z.optional(z.string()),
  fluid: z.optional(z.boolean()),
  restoreEl: z.optional(
    z.union([z.boolean(), isBrowser ? z.instanceof(Element) : z.any()])
  ),
  playsinline: z.optional(z.boolean()),
  controls: z.optional(z.boolean()),
  preload: z.optional(z.enum(['auto', 'metadata', 'none'])),
  liveui: z.optional(z.boolean()),
  theme: z.optional(z.union([z.nativeEnum(Theme), z.literal(false)])),
  adsResponse: z.optional(z.string()),
  adTagUrl: z.optional(z.union([z.string(), z.null()])),
  adUnitCode: z.optional(z.string()),
  adSize: z.optional(z.string()),
  adCustomParams: z.optional(z.string()),
  adDescriptionUrl: z.optional(z.string()),
  blockAd: z.optional(z.boolean()),
  sessionId: z.optional(z.string()),
  html5: z.optional(
    z.object({
      nativeTextTracks: z.optional(z.boolean()),
    })
  ),
  enableSourceset: z.optional(z.boolean()),
  muted: z.optional(z.boolean()),
  inIframe: z.optional(z.boolean()),
  hideFullscreenToggle: z.optional(
    z.union([z.boolean(), z.enum(['inlineonly'])])
  ),
  autoplay: z.optional(
    z.union([z.boolean(), z.enum(['play', 'muted', 'any'])])
  ),
  focusOnInit: z.optional(z.boolean()),
  // TODO pick necessary types from VideoJsPlayerOptions
});

export const UserPlayerOptionsSchema = z.intersection(
  SourceOptionSchema,
  // cannot use zod strict mode as intersection and union is used above
  OtherOptionsSchema
);

export type UserPlayerOptions = z.infer<typeof UserPlayerOptionsSchema>;

export type VideoJsEventCallback = z.infer<typeof VideoJsEventCallbackSchema>;

export type VideoJsOnInitCallback = z.infer<typeof VideoJsOnInitCallbackSchema>;

export type VideoJsOnErrorCallback = z.infer<
  typeof VideoJsOnErrorCallbackSchema
>;

export type VideoJsOnAdErrorCallback = z.infer<
  typeof VideoJsOnAdErrorCallbackSchema
>;

export type OnEventCallback = z.infer<typeof OnEventCallbackSchema>;

// options appended right before creating the player such as in CreatePlayerMixin
export type AppendedOptions = {
  title?: string;
  description?: string;
  mediaServerURL: string;
  onError: VideoJsOnErrorCallback;
  onAdError: VideoJsOnAdErrorCallback;
  onInit?: (playerProxy?: IPlayerProxy) => void;
  // fullscreen: {
  //   options: {
  //     navigationUI: 'show' | 'hide' | 'auto';
  //   };
  // };
};

export type OtherOptions = z.infer<typeof OtherOptionsSchema>;

export type SourceOption = z.infer<typeof SourceOptionSchema>;

export type VideoObj = z.infer<typeof VideoObjSchema>;

export type VideoId = z.infer<typeof VideoIdSchema>;

export enum READY_STATE {
  // HAVE_NOTHING (numeric value 0) No information regarding the media resource is available.
  HAVE_NOTHING = 0,
  // HAVE_METADATA (numeric value 1) Enough of the resource has been obtained that the duration of the resource is available.
  HAVE_METADATA = 1,
  // HAVE_CURRENT_DATA (numeric value 2) Data for the immediate current playback position is available.
  HAVE_CURRENT_DATA = 2,
  // HAVE_FUTURE_DATA (numeric value 3) Data for the immediate current playback position is available, as well as enough data for the user agent to advance the current playback position in the direction of playback.
  HAVE_FUTURE_DATA = 3,
  // HAVE_ENOUGH_DATA (numeric value 4) The user agent estimates that enough data is available for playback to proceed uninterrupted.
  HAVE_ENOUGH_DATA = 4,
}

export type NullableGenericError = PlayerError | Error | null;
